import { type FormEvent, useState } from "react";

import PageShell from "../components/PageShell";
import { postJson, RequestFailedError } from "../lib/api";

/**
 * /delete-account — self-service account deletion (docs/delete-account.md).
 *
 * One route, two modes. Without a token: a public form that takes an email
 * and mails a signed confirmation link (the response never says whether the
 * address has an account). With `?token=` — the emailed link — the visitor
 * must retype their address to finalize; the server enforces the match. The
 * App Store's account-deletion rule (5.1.1(v)) is satisfied by the app
 * driving these same endpoints, with this page as the emailed link's landing
 * spot.
 */

type RequestResponse = {
  status: string;
  /** Local dev only: the confirmation link, since no mail is sent. */
  debug_only_url?: string;
};

const RequestForm = () => {
  const [email, setEmail] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [sent, setSent] = useState<RequestResponse | null>(null);

  const submit = async (event: FormEvent) => {
    event.preventDefault();
    setBusy(true);
    setError(null);
    try {
      const response = await postJson<RequestResponse>(
        "auth/request-account-deletion",
        { email },
      );
      setSent(response ?? { status: "ok" });
    } catch (e) {
      setError(
        e instanceof RequestFailedError
          ? (e.fieldError("email") ?? "Something went wrong. Try again.")
          : "Something went wrong. Try again.",
      );
    } finally {
      setBusy(false);
    }
  };

  if (sent) {
    return (
      <>
        <p className="station__sub">
          If an account exists for <strong>{email}</strong>, we've emailed it a
          confirmation link. Open that link to finish deleting the account. The
          link expires in 24 hours.
        </p>
        {sent.debug_only_url && (
          <p className="station__quiet">
            Local dev: <a href={sent.debug_only_url}>confirmation link</a>
          </p>
        )}
      </>
    );
  }

  return (
    <>
      <p className="station__sub">
        Enter the email address on your account and we'll send a link to
        confirm. Deleting your account removes your profile, station
        memberships, favorites, and comments — permanently.
      </p>
      <form className="delform" onSubmit={submit}>
        <input
          className="delform__input"
          type="email"
          required
          placeholder="you@example.com"
          autoComplete="email"
          value={email}
          onChange={(e) => setEmail(e.target.value)}
        />
        <button className="btn" type="submit" disabled={busy}>
          {busy ? "Sending…" : "Send confirmation link"}
        </button>
        {error && <p className="delform__error">{error}</p>}
      </form>
    </>
  );
};

const ConfirmForm = ({ token }: { token: string }) => {
  const [email, setEmail] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [deadLink, setDeadLink] = useState(false);
  const [done, setDone] = useState(false);

  const submit = async (event: FormEvent) => {
    event.preventDefault();
    setBusy(true);
    setError(null);
    try {
      await postJson("auth/confirm-account-deletion", { token, email });
      setDone(true);
    } catch (e) {
      if (e instanceof RequestFailedError) {
        // A bad or expired token can't be fixed from here; retyping the
        // email can be.
        if (e.fieldError("token")) {
          setDeadLink(true);
          setError(e.fieldError("token") ?? null);
        } else {
          setError(e.fieldError("email") ?? "Something went wrong. Try again.");
        }
      } else {
        setError("Something went wrong. Try again.");
      }
    } finally {
      setBusy(false);
    }
  };

  if (done) {
    return (
      <p className="station__sub">
        Your account has been deleted and can no longer be used. Any remaining
        data will be permanently removed within a few days. Sorry to see you go.
      </p>
    );
  }

  if (deadLink) {
    return (
      <>
        <p className="station__sub">{error}</p>
        <a className="btn" href="/delete-account">
          Request a new link
        </a>
      </>
    );
  }

  return (
    <>
      <p className="station__sub">
        This will permanently delete your StreamLover account — profile, station
        memberships, favorites, and comments. This cannot be undone.
      </p>
      <form className="delform" onSubmit={submit}>
        <label className="delform__label" htmlFor="confirm-email">
          Type your email address to confirm:
        </label>
        <input
          id="confirm-email"
          className="delform__input"
          type="email"
          required
          placeholder="you@example.com"
          autoComplete="email"
          value={email}
          onChange={(e) => setEmail(e.target.value)}
        />
        <button className="btn" type="submit" disabled={busy}>
          {busy ? "Deleting…" : "Permanently delete my account"}
        </button>
        {error && <p className="delform__error">{error}</p>}
      </form>
    </>
  );
};

const DeleteAccountPage = () => {
  // Read once at mount: the token only ever arrives via the emailed link.
  const [token] = useState(
    () => new URLSearchParams(window.location.search).get("token") ?? "",
  );

  return (
    <PageShell>
      <main className="station">
        <p className="eyebrow">Account</p>
        <h1 className="station__name">Delete your account</h1>
        {token ? <ConfirmForm token={token} /> : <RequestForm />}
      </main>
    </PageShell>
  );
};

export default DeleteAccountPage;
